import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, api, type Page, type Template } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { Field, Input, Select, Textarea } from '../ui/field'
import { Notice } from '../chrome/list-screen'
import { Chip } from '../ui/tone'

interface CampaignStep {
  id: string
  stepNumber: number
  delayHours: number
  label: string | null
  isActive: boolean
  templateId: string | null
  template?: { id: string; name: string } | null
}

interface StepsData {
  steps: CampaignStep[]
  followUps: number
  blocking: string[]
  followUpRiskAcceptedAt: string | null
}

export function StepsPanel({ campaignId }: { campaignId: string }) {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [editing, setEditing] = useState<CampaignStep | 'new' | null>(null)

  const steps = useQuery({
    queryKey: ['campaigns', campaignId, 'steps'],
    queryFn: () => api.get<StepsData>(`/campaigns/${campaignId}/steps`),
  })

  const acceptRisk = useMutation({
    mutationFn: () =>
      api.post(`/campaigns/${campaignId}/steps/accept-follow-up-risk`, {
        reason,
      }),
    onSuccess: async () => {
      setAccepting(false)
      setReason('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'steps'] })
      await queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'readiness'] })
    },
    onError: () => setError('Could not accept risk'),
  })

  const remove = useMutation({
    mutationFn: (stepNumber: number) =>
      api.del(`/campaigns/${campaignId}/steps/${stepNumber}`),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'steps'] })
      await queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'readiness'] })
    },
    // The API refuses while the campaign is RUNNING, and says why. Its sentence, not a generic one.
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The step was not removed'),
  })

  if (steps.isLoading) return <p className="text-faint text-[13px]">Loading steps…</p>
  if (!steps.data) return null

  const { steps: stepList, followUps, blocking, followUpRiskAcceptedAt } = steps.data

  return (
    <section>
      <h4 className="text-text m-0 mb-2 text-[13px] font-semibold">Sequence</h4>

      {blocking.length > 0 && (
        <div className="mb-3">
          <Notice tone="warn">
            {blocking[0]}
          </Notice>
        </div>
      )}

      {stepList.length === 0 ? (
        <p className="text-faint text-[13px]">
          No steps yet. A campaign with no step 1 has no wording to send — add one and give it a
          template.
        </p>
      ) : (
        <ul className="m-0 list-none space-y-2 p-0">
          {stepList.map((step) => (
            <li key={step.id} className="flex items-start gap-2 text-[13px]">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Chip tone={step.isActive ? 'ok' : 'neutral'}>
                    Step {step.stepNumber}
                  </Chip>
                  {step.label && <span className="text-muted">{step.label}</span>}
                </div>
                <div className="text-faint mt-1">
                  {step.stepNumber === 1
                    ? 'sent as soon as the recipient is planned'
                    : `${step.delayHours} hours after this recipient's previous send`}{' '}
                  ·{' '}
                  {step.template ? (
                    step.template.name
                  ) : (
                    // Not decoration: a step with no template renders nothing and the dispatcher
                    // refuses it. Saying "no template" in the same grey as everything else read
                    // as a detail rather than as the reason a campaign could not send.
                    <span className="text-bad">no template — this step cannot send</span>
                  )}
                </div>
              </div>
              {can('manage_campaigns') && (
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="quiet" onClick={() => setEditing(step)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="dangerQuiet"
                    disabled={remove.isPending}
                    onClick={() => {
                      setError(null)
                      remove.mutate(step.stepNumber)
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {can('manage_campaigns') && (
        <div className="mt-3">
          <Button size="sm" variant="secondary" icon="plus" onClick={() => setEditing('new')}>
            {stepList.length === 0 ? 'Add the first step' : 'Add a follow-up'}
          </Button>
        </div>
      )}

      {error && (
        <p role="alert" className="text-bad mt-2 text-[12.5px]">
          {error}
        </p>
      )}

      {editing && (
        <StepDialog
          campaignId={campaignId}
          step={editing === 'new' ? null : editing}
          nextNumber={Math.max(0, ...stepList.map((x) => x.stepNumber)) + 1}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Accept follow-up risk — only needed if there are follow-ups and they haven't been accepted yet */}
      {followUps > 0 && !followUpRiskAcceptedAt && can('manage_campaigns') && (
        <div className="border-line mt-4 border-t pt-4">
          <Button variant="secondary" onClick={() => setAccepting(true)}>
            Accept follow-up risk
          </Button>
          <p className="text-faint mt-2 text-[12.5px]">
            This campaign has {followUps} follow-up step{followUps === 1 ? '' : 's'}. Mail Room cannot
            detect replies, so a follow-up may reach someone who already answered. Record how replies
            will be noticed before taking this campaign out of dry run.
          </p>
        </div>
      )}

      {/* Show acceptance status */}
      {followUpRiskAcceptedAt && (
        <div className="text-muted mt-4 text-[13px]">
          Follow-up risk accepted on {new Date(followUpRiskAcceptedAt).toLocaleDateString()}
        </div>
      )}

      <Dialog
        open={accepting}
        title="Accept follow-up risk"
        description="This campaign has follow-ups. Mail Room cannot see replies, so a follow-up can reach someone who already answered."
        onClose={() => {
          setAccepting(false)
          setError(null)
          setReason('')
        }}
        footer={
          <>
            <Button variant="quiet" onClick={() => setAccepting(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={reason.length < 20 || acceptRisk.isPending}
              onClick={() => acceptRisk.mutate()}
            >
              {acceptRisk.isPending ? 'Accepting…' : 'Accept'}
            </Button>
          </>
        }
      >
        <Field
          label="How replies will be noticed"
          error={error}
          hint="Minimum 20 characters. Describe what will catch replies instead, since Mail Room cannot."
        >
          {(props) => (
            <Textarea
              {...props}
              value={reason}
              placeholder="n8n watches the mailbox for replies and pauses the sequence…"
              onChange={(e) => {
                setReason(e.target.value)
                setError(null)
              }}
              rows={4}
              maxLength={500}
            />
          )}
        </Field>
      </Dialog>
    </section>
  )
}

/**
 * Adding or replacing one step — §37, and the control that was missing until 2026-08-27.
 *
 * `POST /campaigns/:id/steps` landed with sequences on 2026-08-23 and **nothing called it**. A
 * campaign could be created, given recipients, reviewed and planned entirely from the console and
 * then send nothing, because attaching wording to it was only possible over the API. That is the
 * same gap G040 found in templates: an endpoint with no button is a feature the product does not
 * have.
 *
 * Two rules the API enforces are taught here rather than left to an error message:
 *
 * - **Step 1 has no delay.** It is the first message, not the first follow-up, and goes as soon as
 *   the recipient is planned. The field is disabled rather than hidden, so the rule is visible.
 * - **A follow-up's floor is 12 hours**, measured from *that recipient's own* previous send. An
 *   hour later is not a follow-up; the receiving domain reads it as a second message.
 */
function StepDialog({
  campaignId,
  step,
  nextNumber,
  onClose,
}: {
  campaignId: string
  step: CampaignStep | null
  nextNumber: number
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [stepNumber, setStepNumber] = useState(String(step?.stepNumber ?? nextNumber))
  const [delayHours, setDelayHours] = useState(String(step?.delayHours ?? 48))
  const [templateId, setTemplateId] = useState(step?.templateId ?? step?.template?.id ?? '')
  const [label, setLabel] = useState(step?.label ?? '')
  const [error, setError] = useState<string | null>(null)

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Page<Template>>('/templates?take=200'),
  })

  const isFirst = Number(stepNumber) === 1

  const save = useMutation({
    mutationFn: () =>
      api.post(`/campaigns/${campaignId}/steps`, {
        stepNumber: Number(stepNumber),
        // The API's floor is 12 for every step. Step 1 never waits — the planner sends it as soon
        // as the recipient is scheduled — so the value is irrelevant rather than zero.
        delayHours: isFirst ? 12 : Number(delayHours),
        templateId: templateId || undefined,
        label: label.trim() || undefined,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'steps'] }),
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'readiness'] }),
      ])
      onClose()
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The step was not saved'),
  })

  const items = templates.data?.items ?? []
  // A template whose latest version has no subject has never been written into. Offering it would
  // build a step that renders an empty message and is refused by §30 at send time.
  const usable = items.filter((t) => t.versions[0]?.subjectTemplate)

  const blockedBecause =
    !Number.isInteger(Number(stepNumber)) || Number(stepNumber) < 1
      ? 'A step number is 1 or higher'
      : !isFirst && Number(delayHours) < 12
        ? 'A follow-up waits at least 12 hours — sooner reads as a second message, not a follow-up'
        : !templateId
          ? 'Choose a template. A step without one renders nothing and is refused at send time'
          : null

  return (
    <Dialog
      open
      width={520}
      title={step ? `Step ${step.stepNumber}` : 'Add a step'}
      description="A step is one message in the sequence. Each carries its own template — a follow-up repeating the first message word for word reads as a malfunction rather than as a person."
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={blockedBecause !== null || save.isPending}
            title={blockedBecause ?? undefined}
            aria-label={
              blockedBecause ? `Save step — unavailable: ${blockedBecause}` : 'Save step'
            }
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            {save.isPending ? 'Saving…' : 'Save step'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p role="alert" className="text-bad text-[12.5px]">
            {error}
          </p>
        )}

        <Field
          label="Step number"
          hint="1 is the first message. 2 and above are follow-ups, sent in order."
        >
          {(props) => (
            <Input
              {...props}
              value={stepNumber}
              inputMode="numeric"
              disabled={Boolean(step)}
              onChange={(e) => setStepNumber(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Template"
          hint={
            usable.length === 0
              ? 'No template has any wording yet. Write one on the Templates screen first.'
              : 'The wording this step sends. Only templates that have been written are listed.'
          }
        >
          {(props) => (
            <Select {...props} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Choose a template…</option>
              {usable.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.versions[0]?.approvedAt ? '' : ' — not approved'}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Delay"
          hint={
            isFirst
              ? 'Step 1 has no delay — it is sent as soon as the recipient is planned.'
              : 'Hours after this recipient’s own previous send, never after the campaign started. Minimum 12.'
          }
        >
          {(props) => (
            <Input
              {...props}
              value={isFirst ? '' : delayHours}
              placeholder={isFirst ? 'sent immediately' : '48'}
              inputMode="numeric"
              disabled={isFirst}
              onChange={(e) => setDelayHours(e.target.value)}
            />
          )}
        </Field>

        <Field label="Label" hint="Optional. What this step is for, in your own words.">
          {(props) => (
            <Input
              {...props}
              value={label}
              placeholder="First approach"
              onChange={(e) => setLabel(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
